import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ArrowRight,
  CalendarClock,
  TrendingDown,
  Home,
  Scroll,
  Wrench,
  Users,
  Scale,
  FileWarning,
  Trees,
  Check,
  CircleSlash,
} from "lucide-react";

import { SITE, ABOUT_BG } from "../lib/config";
import { IbexMark } from "../components/site/IbexMark";
import LeadForm from "../components/site/LeadForm";

const scrollToId = (id) => (e) => {
  e && e.preventDefault && e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------------- HERO ---------------- */
const WhyHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: "640px" }}
      data-testid="why-hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={ABOUT_BG}
          alt=""
          className="absolute inset-0 w-full h-[120%] object-cover"
        />
      </motion.div>

      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(115deg, rgba(18,43,32,0.82) 0%, rgba(18,43,32,0.55) 50%, rgba(18,43,32,0.20) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 10% 92%, rgba(18,43,32,0.75) 0%, rgba(18,43,32,0) 60%)",
        }}
      />

      <div className="relative z-10 h-full flex items-end lg:items-center">
        <div className="max-w-7xl mx-auto w-full px-6 md:px-10 pt-32 pb-24 lg:py-32">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="eyebrow"
            data-testid="why-hero-eyebrow"
          >
            Why We Do This
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: "easeOut" }}
            className="font-heading font-medium text-[#FAF7F2] mt-5 max-w-3xl"
            style={{
              fontSize: "clamp(2.6rem, 5.4vw, 4.6rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.015em",
            }}
            data-testid="why-hero-headline"
          >
            Hard Chapters Happen.{" "}
            <span className="gold-italic">They Don’t Get the Last Word.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: "easeOut" }}
            className="mt-7 max-w-2xl text-[#FAF7F2]/90 font-body text-base md:text-lg"
            style={{ lineHeight: 1.7 }}
          >
            Ibex exists for the moments when a property stops being a home and
            starts being a weight — a mortgage that got away from you, a house
            you inherited, a rental you’re done carrying. We buy it, close on
            your timeline, and put it back to work in the community. Here’s why
            that matters to us, and what it means for you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={scrollToId("offer-form")}
              className="btn-primary"
              data-testid="why-hero-cta-primary"
            >
              Get My Cash Offer
            </button>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#honest"
        onClick={scrollToId("honest")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#C6A15B]"
        aria-label="Scroll to next section"
      >
        <span className="text-[0.6rem]" style={{ letterSpacing: "0.32em", textTransform: "uppercase" }}>
          Scroll
        </span>
        <div className="w-px h-10 bg-[#C6A15B]/60" />
        <ChevronDown size={18} className="scroll-indicator" strokeWidth={1.5} />
      </motion.a>
    </section>
  );
};

/* ---------------- HONEST PART ---------------- */
const HonestPart = () => (
  <section
    id="honest"
    className="section-pad"
    style={{ background: "#FAF7F2" }}
    data-testid="why-honest-section"
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <p className="eyebrow">First, the Honest Part</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          You Might Expect a Pitch Here.{" "}
          <span className="gold-italic">This Isn’t One.</span>
        </h2>
        <div className="mt-10 space-y-6">
          <p className="font-body text-[#2B2926]/90" style={{ fontSize: "1.08rem", lineHeight: 1.85 }}>
            If you’ve searched anything about foreclosure lately, you’ve seen
            the ads — the ones shouting deadlines at you in capital letters.
            That’s not how we work, and it’s not what this page is for.
          </p>
          <p className="font-body text-[#2B2926]/90" style={{ fontSize: "1.08rem", lineHeight: 1.85 }}>
            This page gives you the plain facts: what’s happening in the market
            right now, what a foreclosure actually does to your next several
            years, and what your realistic options are — including the ones
            that don’t involve us. Read it, take what’s useful, and call if it
            makes sense. If it doesn’t, we hope it helped anyway.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---------------- STATS ---------------- */
const stats = [
  {
    n: "227,548",
    label:
      "U.S. homes with a foreclosure filing in the first half of 2026 — up 21% from a year earlier.",
    source: "Source: ATTOM Mid-Year 2026 Foreclosure Market Report",
  },
  {
    n: "+57%",
    label:
      "Colorado’s year-over-year rise in foreclosure filings in the first half of 2026 — one of the steepest increases of any state, and more than double the national pace.",
    source: "Source: ATTOM",
  },
  {
    n: "1 in 657",
    label:
      "Colorado homes with a foreclosure filing in the first half of 2026.",
    source: "Source: ATTOM",
  },
  {
    n: "+33%",
    label:
      "Increase in completed foreclosures — homes actually repossessed — nationwide, first half of 2026 vs. 2025.",
    source: "Source: ATTOM",
  },
];

const Stats = () => (
  <section
    id="why-stats"
    className="section-pad"
    style={{ background: "#F0EAE0" }}
    data-testid="why-stats-section"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-3xl"
      >
        <p className="eyebrow">The Bigger Picture</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          If You’re Behind,{" "}
          <span className="gold-italic">You’re in a Lot of Company.</span>
        </h2>
        <p className="mt-6 font-body text-[#2B2926]/90 max-w-2xl" style={{ fontSize: "1.05rem", lineHeight: 1.8 }}>
          Foreclosures are rising again — not because a wave of people suddenly
          got careless, but because the math changed. Payments, insurance, HOA
          dues, and property taxes all climbed at once, and a lot of households
          that were doing fine in 2021 are stretched thin in 2026. Falling
          behind in this market is a financial event, not a character flaw.
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
      >
        {stats.map((s, i) => (
          <motion.article
            key={i}
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="glass-light p-7 md:p-8 flex flex-col"
            data-testid={`why-stat-${i}`}
          >
            <div
              className="font-heading font-medium leading-none"
              style={{ color: "#C6A15B", fontSize: "clamp(2.4rem, 4vw, 3rem)", letterSpacing: "-0.02em" }}
            >
              {s.n}
            </div>
            <p className="mt-5 font-body text-[#2B2926]/90 flex-1" style={{ fontSize: "0.95rem", lineHeight: 1.65 }}>
              {s.label}
            </p>
            <p className="mt-5" style={{ color: "#A8A29A", fontSize: "0.72rem", lineHeight: 1.5 }}>
              {s.source}
            </p>
          </motion.article>
        ))}
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="mt-12 font-body text-[#2B2926]/90 max-w-3xl"
        style={{ fontSize: "1.05rem", lineHeight: 1.8 }}
      >
        One more number worth knowing if you’re in Colorado: once a lender
        files, the public trustee sets the sale date 110 to 125 days out.
        That’s about four months — enough time to make a good decision, not
        enough to wait and see.
      </motion.p>

      <p className="mt-8 max-w-3xl" style={{ color: "#A8A29A", fontSize: "0.75rem", lineHeight: 1.65 }}>
        Sources: ATTOM Data Solutions, Mid-Year 2026 U.S. Foreclosure Market
        Report and July 2026 U.S. Foreclosure Market Report; Colorado public
        trustee timeline per C.R.S. 38-38-108. Figures are national and
        statewide market data, not a prediction about any individual property.
      </p>
    </div>
  </section>
);

/* ---------------- CREDIT ---------------- */
const creditFacts = [
  {
    icon: CalendarClock,
    lead: "Seven years.",
    body:
      "A foreclosure stays on your credit report for seven years from the first missed payment that started it.",
    source: "Source: Experian",
  },
  {
    icon: TrendingDown,
    lead: "85 to 160 points.",
    body:
      "In FICO’s own research, a 680 score typically drops 85–105 points after a foreclosure; a 780 score drops 140–160.",
    source: "Source: FICO, as reported by Nolo",
  },
  {
    icon: Home,
    lead: "Seven years to buy again.",
    body:
      "Conventional lenders generally require a seven-year wait after a foreclosure before writing a new mortgage. After a short sale or deed-in-lieu, it’s usually four. FHA is typically three either way.",
    source: "Source: Fannie Mae and FHA guidelines",
  },
];

const CreditSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const midY = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);

  return (
    <section
      id="why-credit"
      ref={ref}
      className="relative overflow-hidden section-pad"
      style={{ background: "#122B20", color: "#FAF7F2" }}
      data-testid="why-credit-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0" aria-hidden="true">
        <img src={ABOUT_BG} alt="" className="absolute inset-0 w-full h-[120%] object-cover" loading="lazy" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(18,43,32,0.85) 0%, rgba(18,43,32,0.92) 100%)",
          }}
        />
      </motion.div>

      <motion.div
        style={{ y: midY }}
        className="pointer-events-none absolute right-[-4%] top-1/2 -translate-y-1/2 w-[560px] h-[560px] md:w-[720px] md:h-[720px]"
        aria-hidden="true"
      >
        <IbexMark className="w-full h-full" strokeWidth={1} />
        <style>{`
          [data-testid="why-credit-section"] svg { color: rgba(198,161,91,0.09); }
        `}</style>
      </motion.div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <p className="eyebrow">The Part That Follows You</p>
          <h2
            className="font-heading font-medium mt-4 max-w-3xl"
            style={{
              fontSize: "clamp(2rem, 4.2vw, 3.6rem)",
              lineHeight: 1.1,
              color: "#FAF7F2",
              letterSpacing: "-0.015em",
            }}
          >
            The Goal Isn’t Just Selling the House.{" "}
            <span className="gold-italic">It’s Protecting the Next Ten Years.</span>
          </h2>
          <p
            className="mt-8 font-body max-w-2xl"
            style={{ color: "rgba(250,247,242,0.88)", fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            A completed foreclosure is one of the heaviest marks a credit
            report can carry. Three facts worth knowing before you decide
            anything:
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          className="mt-14 space-y-5 md:space-y-6"
        >
          {creditFacts.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={i}
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="glass-dark p-6 md:p-8 flex gap-5 md:gap-6 items-start"
                data-testid={`why-credit-fact-${i}`}
              >
                <div
                  className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(198,161,91,0.14)",
                    border: "1px solid rgba(198,161,91,0.55)",
                  }}
                >
                  <Icon size={18} color="#C6A15B" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <p
                    className="font-body"
                    style={{ color: "#FAF7F2", fontSize: "1rem", lineHeight: 1.65 }}
                  >
                    <span
                      className="font-heading font-medium"
                      style={{ color: "#C6A15B", fontSize: "1.05rem" }}
                    >
                      {f.lead}
                    </span>{" "}
                    {f.body}
                  </p>
                  <p className="mt-2" style={{ color: "rgba(168,162,154,0.85)", fontSize: "0.72rem" }}>
                    {f.source}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-14 font-body max-w-3xl"
          style={{ color: "rgba(250,247,242,0.88)", fontSize: "1.05rem", lineHeight: 1.8 }}
        >
          Selling before the sale date changes that math. If your home has
          equity, a sale pays the loan off at closing — the foreclosure is
          never recorded, and you walk away with what’s left. If you owe more
          than the home is worth, we’ll tell you that plainly and explain what
          a short sale involves, because that’s a different road with its own
          rules.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-10 pl-5 md:pl-6"
          style={{ borderLeft: "2px solid #C6A15B" }}
        >
          <p
            className="font-body italic"
            style={{ color: "#C6A15B", fontSize: "1.02rem", lineHeight: 1.7 }}
          >
            What a sale can’t do: erase late payments that have already been
            reported. We’d rather you hear that from us now than from a lender
            later.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- THREE STEPS ---------------- */
const steps = [
  {
    n: "01",
    title: "Tell us where you stand.",
    body:
      "The sale date if there is one, what’s owed, what the house needs. Two minutes on the form or one phone call. We’ve heard every version of this story, and none of them surprised us.",
  },
  {
    n: "02",
    title: "Get a real number and a real read.",
    body:
      "A written cash offer, usually within 48 hours, and a plain explanation of how we got there. If selling to us isn’t your best move, we’ll say so.",
  },
  {
    n: "03",
    title: "Close before the deadline.",
    body:
      "We work with a licensed title company, coordinate the payoff with your lender, and close on a date that beats the sale. Loan paid. Remaining equity to you. Foreclosure never recorded.",
  },
];

const ThreeSteps = () => (
  <section
    id="why-steps"
    className="section-pad"
    style={{ background: "#FAF7F2" }}
    data-testid="why-steps-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl"
      >
        <p className="eyebrow">How It Works When the Clock Is Running</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          Three Steps.{" "}
          <span className="gold-italic">Before the Sale Date.</span>
        </h2>
      </motion.div>

      <div className="relative mt-16 md:mt-20">
        <div
          className="hidden md:block absolute top-[3.5rem] left-[16.66%] right-[16.66%] h-px"
          style={{
            background:
              "linear-gradient(90deg, rgba(198,161,91,0) 0%, rgba(198,161,91,0.55) 20%, rgba(198,161,91,0.55) 80%, rgba(198,161,91,0) 100%)",
          }}
        />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative"
        >
          {steps.map((s) => (
            <motion.article
              key={s.n}
              variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="glass-light p-8 md:p-9 relative"
              data-testid={`why-step-${s.n}`}
            >
              <div
                className="font-heading font-medium mb-6 leading-none"
                style={{ fontSize: "2.6rem", color: "#C6A15B", letterSpacing: "-0.02em" }}
              >
                {s.n}
              </div>
              <h3 className="font-heading font-medium text-2xl" style={{ color: "#122B20" }}>
                {s.title}
              </h3>
              <p className="mt-4 font-body text-[#2B2926]/85" style={{ fontSize: "0.98rem", lineHeight: 1.7 }}>
                {s.body}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- BEYOND FORECLOSURE ---------------- */
const beyond = [
  {
    icon: Scroll,
    title: "Inherited & Probate",
    body: "A house three states away that you never asked for. We handle it remotely, paperwork included.",
  },
  {
    icon: Wrench,
    title: "Repairs You Can’t Fund",
    body: "Roof, foundation, fire or water damage. We buy it exactly as it sits.",
  },
  {
    icon: Users,
    title: "Tired Landlords",
    body: "Tenants in place, late rent, midnight calls. We take it over as-is.",
  },
  {
    icon: Scale,
    title: "Divorce or Relocation",
    body: "When the house needs to be settled cleanly and on a date you can count on.",
  },
  {
    icon: FileWarning,
    title: "Back Taxes & Title Tangles",
    body: "Liens are paid from the proceeds at closing. Our title company sorts the rest.",
  },
  {
    icon: Trees,
    title: "Land You’re Done Paying Taxes On",
    body: "Vacant lots, acreage, inherited parcels. Turn an idle bill into cash.",
  },
];

const BeyondForeclosure = () => (
  <section
    id="why-beyond"
    className="section-pad"
    style={{ background: "#F0EAE0" }}
    data-testid="why-beyond-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl"
      >
        <p className="eyebrow">Beyond Foreclosure</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          Any Property That’s Become{" "}
          <span className="gold-italic">a Weight.</span>
        </h2>
        <p className="mt-5 font-body text-[#2B2926]/85 max-w-2xl" style={{ fontSize: "1.05rem", lineHeight: 1.7 }}>
          Foreclosure is the loudest version of this problem, but it isn’t the
          only one. We buy houses and land in every one of these situations,
          as-is, with no repairs, cleanouts, or commissions.
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
      >
        {beyond.map((it, i) => {
          const Icon = it.icon;
          return (
            <motion.article
              key={i}
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="group relative p-7 md:p-8 rounded-2xl transition-all duration-300"
              style={{ background: "#FAF7F2", border: "1px solid rgba(168,162,154,0.35)" }}
              data-testid={`why-beyond-${i}`}
            >
              <div className="flex items-start justify-between">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(27,67,50,0.06)", border: "1px solid rgba(27,67,50,0.15)" }}
                >
                  <Icon size={20} color="#1B4332" strokeWidth={1.5} />
                </div>
                <div
                  className="font-heading text-sm opacity-40 group-hover:opacity-100 transition-opacity"
                  style={{ color: "#C6A15B" }}
                >
                  0{i + 1}
                </div>
              </div>
              <h3
                className="font-heading font-medium mt-6 text-xl md:text-[1.25rem]"
                style={{ color: "#122B20", lineHeight: 1.25 }}
              >
                {it.title}
              </h3>
              <p className="mt-3 font-body text-[#2B2926]/80" style={{ fontSize: "0.94rem", lineHeight: 1.65 }}>
                {it.body}
              </p>
              <style>{`
                [data-testid="why-beyond-${i}"]:hover {
                  transform: translateY(-4px);
                  box-shadow: 0 24px 40px -24px rgba(18,43,32,0.25);
                  border-color: rgba(198,161,91,0.4);
                }
              `}</style>
            </motion.article>
          );
        })}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="mt-12 text-center"
      >
        <Link
          to="/#what-we-buy"
          className="inline-flex items-center gap-2 group"
          data-testid="why-see-everything-link"
          style={{
            color: "#C6A15B",
            fontSize: "0.9rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          <span className="border-b border-[#C6A15B]/50 group-hover:border-[#C6A15B] pb-0.5 transition-colors">
            See everything we buy
          </span>
          <ArrowRight size={16} strokeWidth={1.75} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ---------------- DEVELOPMENT MISSION ---------------- */
const Mission = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section
      id="why-mission"
      ref={ref}
      className="relative overflow-hidden section-pad"
      style={{ background: "#122B20", color: "#FAF7F2" }}
      data-testid="why-mission-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0" aria-hidden="true">
        <img src={ABOUT_BG} alt="" className="absolute inset-0 w-full h-[120%] object-cover" loading="lazy" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(18,43,32,0.88) 0%, rgba(18,43,32,0.94) 100%)",
          }}
        />
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <p className="eyebrow">Where It Goes From Here</p>
          <h2
            className="font-heading font-medium mt-4 max-w-3xl"
            style={{
              fontSize: "clamp(2rem, 4.2vw, 3.6rem)",
              lineHeight: 1.1,
              color: "#FAF7F2",
              letterSpacing: "-0.015em",
            }}
          >
            Good Homes at{" "}
            <span className="gold-italic">Every Price Point.</span>
          </h2>
          <div className="mt-10 space-y-6 max-w-2xl">
            <p className="font-body" style={{ color: "rgba(250,247,242,0.9)", fontSize: "1.05rem", lineHeight: 1.85 }}>
              Buying property is half of what we do. The other half is what
              happens next. We’re building toward development on both ends of
              the market: high-end homes that raise the standard on a street,
              and well-built, honestly priced homes that give working families
              real choices about where to live.
            </p>
            <p className="font-body" style={{ color: "rgba(250,247,242,0.85)", fontSize: "1.05rem", lineHeight: 1.85 }}>
              We think the two belong together. A neighborhood is healthier
              when a teacher and a surgeon can both find a good house in it,
              and the lots that make that possible are usually the ones nobody
              else wanted — the complicated parcels, the tired houses, the
              properties someone needed to let go of. That’s the terrain we’re
              built for.
            </p>
            <p className="font-body" style={{ color: "rgba(250,247,242,0.85)", fontSize: "1.05rem", lineHeight: 1.85 }}>
              We’re early in this chapter. Our first projects are being
              planned, not photographed, and we won’t put a rendering on this
              page and call it a track record. What we can promise is this: the
              property you sell us doesn’t vanish into a spreadsheet. It gets a
              next chapter we’d be proud to put our name on.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- FIT ---------------- */
const fitYes = [
  "You have equity and a sale date that’s getting close",
  "The house needs work you can’t or don’t want to fund",
  "You’ve decided to move on and want it done cleanly, on a date you choose",
  "You want to talk to the actual people buying it, not a call center",
];
const fitNo = [
  {
    line: "You can catch up and want to stay.",
    sub:
      "Call the Colorado Foreclosure Hotline first — 1-877-601-4673 — for free, HUD-approved counseling. Outside Colorado: 888-995-4673.",
  },
  { line: "You’re waiting on a loan modification decision from your lender" },
  { line: "You have time and the house would net you more on the open market" },
];

const FitSection = () => (
  <section
    id="why-fit"
    className="section-pad"
    style={{ background: "#FAF7F2" }}
    data-testid="why-fit-section"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-3xl"
      >
        <p className="eyebrow">Straight Answer</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          When Ibex Is the Right Call —{" "}
          <span className="gold-italic">and When It Isn’t.</span>
        </h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
        className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-stretch"
      >
        <motion.div
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="glass-light p-7 md:p-8 h-full flex flex-col"
          data-testid="why-fit-yes"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: "rgba(198,161,91,0.14)", border: "1px solid rgba(198,161,91,0.55)" }}
            >
              <Check size={18} color="#C6A15B" strokeWidth={2.25} />
            </div>
            <h3 className="font-heading font-medium" style={{ color: "#122B20", fontSize: "1.3rem" }}>
              Ibex is probably the right call if…
            </h3>
          </div>
          <ul className="mt-6 space-y-3.5">
            {fitYes.map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className="shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(198,161,91,0.18)", border: "1px solid rgba(198,161,91,0.6)" }}
                >
                  <Check size={10} color="#C6A15B" strokeWidth={2.5} />
                </div>
                <span className="font-body text-[#2B2926]" style={{ fontSize: "0.98rem", lineHeight: 1.6 }}>
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="p-7 md:p-8 rounded-2xl h-full flex flex-col"
          style={{ background: "rgba(250,247,242,0.55)", border: "1px solid rgba(168,162,154,0.45)" }}
          data-testid="why-fit-no"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: "rgba(168,162,154,0.15)", border: "1px solid rgba(168,162,154,0.55)" }}
            >
              <CircleSlash size={18} color="#A8A29A" strokeWidth={1.75} />
            </div>
            <h3 className="font-heading font-medium" style={{ color: "#2B2926", fontSize: "1.3rem" }}>
              Probably not — and we’ll tell you
            </h3>
          </div>
          <ul className="mt-6 space-y-4">
            {fitNo.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className="shrink-0 mt-1 w-4 h-4 rounded-full"
                  style={{ border: "1px solid rgba(168,162,154,0.7)", background: "transparent" }}
                />
                <div>
                  <p className="font-body text-[#2B2926]/90" style={{ fontSize: "0.98rem", lineHeight: 1.55 }}>
                    {item.line}
                  </p>
                  {item.sub && (
                    <p
                      className="mt-1"
                      style={{ color: "#A8A29A", fontSize: "0.82rem", lineHeight: 1.55, fontStyle: "italic" }}
                    >
                      {item.sub}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="mt-14 md:mt-16 text-center font-heading"
        style={{
          color: "#122B20",
          fontSize: "clamp(1.25rem, 2vw, 1.6rem)",
          lineHeight: 1.35,
        }}
      >
        We’d rather lose a deal{" "}
        <span className="gold-italic">than earn one that wasn’t good for you.</span>
      </motion.p>
    </div>
  </section>
);

/* ---------------- FOUNDERS ---------------- */
const Founders = () => (
  <section
    id="why-founders"
    className="section-pad"
    style={{ background: "#F0EAE0" }}
    data-testid="why-founders-section"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <div
            className="w-full max-w-sm aspect-[4/5] rounded-2xl mx-auto relative overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, rgba(198,161,91,0.20) 0%, rgba(27,67,50,0.55) 100%)",
              border: "1px solid rgba(198,161,91,0.5)",
              boxShadow: "0 40px 60px -40px rgba(18,43,32,0.35)",
            }}
            data-testid="why-founders-card"
          >
            <img
              src={SITE.founderPhoto}
              alt="Brandon and Katana — founders of Ibex Acquisitions & Development"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute bottom-0 inset-x-0 h-24"
              style={{
                background:
                  "linear-gradient(180deg, rgba(18,43,32,0) 0%, rgba(18,43,32,0.7) 100%)",
              }}
            />
            <div className="absolute bottom-5 inset-x-0 text-center">
              <p className="font-heading" style={{ color: "#FAF7F2", fontSize: "1.05rem" }}>
                Brandon &amp; Katana
              </p>
              <p
                className="mt-1"
                style={{
                  color: "#C6A15B",
                  fontSize: "0.68rem",
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                }}
              >
                Founders
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <p className="eyebrow">From Brandon &amp; Katana</p>
          <h2
            className="font-heading font-medium mt-4"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.4rem)",
              lineHeight: 1.1,
              color: "#122B20",
              letterSpacing: "-0.015em",
            }}
          >
            Why We Put Our Names{" "}
            <span className="gold-italic">on This.</span>
          </h2>
          <p
            className="mt-6 font-body text-[#2B2926]/90 max-w-2xl"
            style={{ fontSize: "1.05rem", lineHeight: 1.85 }}
          >
            We built Ibex as a family company on purpose. When you call, you
            reach the people whose reputation rides on how this turns out — not
            a bullpen, not a script. Straight talk, fair numbers, and
            follow-through were the rules when we only bought land, and they’re
            the rules now that we buy homes. Whatever chapter you’re in, we’ll
            meet you there and tell you the truth about it.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- FORM WRAPPER ---------------- */
const FormLeadIn = () => (
  <section
    id="why-form-leadin"
    style={{
      background: "#FAF7F2",
      paddingTop: "clamp(3rem, 6vw, 4.5rem)",
      paddingBottom: "0",
    }}
    data-testid="why-form-leadin"
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="font-body text-[#2B2926]/90 mx-auto"
        style={{ fontSize: "1.05rem", lineHeight: 1.75, maxWidth: "56ch" }}
      >
        If there’s a sale date on the calendar, put it in the message box —{" "}
        <span className="gold-italic">it moves you to the front of the line.</span>
      </motion.p>
    </div>
  </section>
);

/* ---------------- DISCLAIMER ---------------- */
const WhyDisclaimer = () => (
  <section
    style={{ background: "#FAF7F2", paddingTop: 0, paddingBottom: "3rem" }}
    data-testid="why-disclaimer"
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10">
      <p style={{ color: "#A8A29A", fontSize: "0.75rem", lineHeight: 1.65 }}>
        Ibex Acquisitions &amp; Development is a real estate investment
        company, not a law firm, lender, or credit counselor. Market, credit,
        and lending figures on this page come from the sources cited and are
        general information, not advice about your situation. Offers are
        subject to standard due diligence and title review.
      </p>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
const WhyPage = () => (
  <main data-testid="why-page">
    <WhyHero />
    <HonestPart />
    <Stats />
    <CreditSection />
    <ThreeSteps />
    <BeyondForeclosure />
    <Mission />
    <FitSection />
    <Founders />
    <FormLeadIn />
    <LeadForm />
    <WhyDisclaimer />
  </main>
);

export default WhyPage;
